import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslate } from "../../lib/translation";
import Checkbox from "@dt/Checkbox/Checkbox";
import { FormField } from "@dt/FormField";
import styles from "./CheckboxGroup.module.css";

export interface CheckboxGroupProps {
  /** Base id for the checkbox inputs. Without one, the group generates a unique base id, so two groups on a page never share checkbox ids. */
  id?: string;
  /** Legend text for the group */
  label: string;
  /** Additional CSS classes on the group */
  className?: string;
  /** Checkbox options (label, value) */
  options: { label: string; value: string }[];
  /**
   * Shows the select-all master checkbox. Defaults to true.
   * @default true 
   */
  showMasterCheckbox?: boolean;
  /** Label for the select-all master checkbox */
  masterLabel?: string;
  /** Keeps the legend as the accessible name but hides it visually, for a group whose section heading already names it. @default false */
  hideLegend?: boolean;
  /** Initially selected option values (uncontrolled) */
  defaultSelected?: string[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (selectedOptions: string[]) => void;
}

/** Checkbox group with optional master select-all control. */
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  id,
  label,
  className,
  options = [],
  showMasterCheckbox = true,
  hideLegend = false,
  defaultSelected,
  masterLabel,
  onChange,
}) => {
  const t = useTranslate();
  const generatedId = useId();
  const baseId = id || generatedId;
  const [checkedStates, setCheckedStates] = useState(options.map(() => false));
  // Track previous option values to avoid resetting when only labels change
  const prevOptionValuesRef = useRef<string[] | null>(null);
  const prevDefaultRef = useRef<string[] | undefined>(undefined);

  // Reset states only when the option values or defaultSelected change
  useEffect(() => {
    const currentOptionValues = options.map((opt) => opt.value);

    const valuesChanged =
      !prevOptionValuesRef.current ||
      prevOptionValuesRef.current.length !== currentOptionValues.length ||
      prevOptionValuesRef.current.some((v, i) => v !== currentOptionValues[i]);

    const defaultChanged =
      (prevDefaultRef.current || []).join(",") !==
      (defaultSelected || []).join(",");

    if (valuesChanged || defaultChanged) {
      setCheckedStates(
        options.map((opt) =>
          defaultSelected ? defaultSelected.includes(opt.value) : false,
        ),
      );
    }

    prevOptionValuesRef.current = currentOptionValues;
    prevDefaultRef.current = defaultSelected;
  }, [options, defaultSelected]);
  // Calculate master checkbox state for props
  const allChecked = checkedStates.every((state) => state);
  const noneChecked = checkedStates.every((state) => !state);
  const someChecked = !allChecked && !noneChecked;
  const handleMasterCheckboxChange = (checked: boolean) => {
    const newCheckedStates = options.map(() => checked);
    setCheckedStates(newCheckedStates);

    if (onChange) {
      const selectedOptions = newCheckedStates
        .map((state, index) => (state ? options[index].value : ""))
        .filter(Boolean);
      onChange(selectedOptions);
    }
  };

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const newCheckedStates = [...checkedStates];
    newCheckedStates[index] = checked;
    setCheckedStates(newCheckedStates);

    if (onChange) {
      const selectedOptions = newCheckedStates
        .map((state, index) => (state ? options[index].value : ""))
        .filter(Boolean);
      onChange(selectedOptions);
    }
  };

  // fieldset/legend gives the group its name. The previous <label for>
  // pointed at a nonexistent id when the master checkbox was hidden, so the
  // checkboxes had no group name at all, and with the master shown it
  // doubled the master's own label.
  return (
    <FormField
      legend={label}
      hideLegend={hideLegend}
      className={`${styles["checkboxGroup"]} ${className || ""}`.trim()}
    >
      {showMasterCheckbox && (
        <Checkbox
          label={(() => {
            const translated = t("contactAll");
            // If no translator is injected in tests, t may return the key itself.
            // In that case fall back to the literal "All" to keep tests stable.
            if (masterLabel) return masterLabel;
            if (!translated || translated === "contactAll") return "All";
            return translated;
          })()}
          checked={allChecked}
          indeterminate={someChecked}
          onCheckedChange={(checked: boolean) =>
            handleMasterCheckboxChange(checked)
          }
          id={`masterCheckbox-${baseId}`}
        />
      )}
      <div className={styles.options}>
        {options.map((option, index) => {
          const optionId = `${baseId}-option-${option.value || index}`;
          return (
            <Checkbox
              key={option.value}
              label={option.label}
              checked={checkedStates[index]}
              onCheckedChange={(checked: boolean) =>
                handleCheckboxChange(index, checked)
              }
              id={optionId}
            />
          );
        })}
      </div>
    </FormField>
  );
};

CheckboxGroup.displayName = "CheckboxGroup";

export default CheckboxGroup;
